/**
 * Patient programmes (Phase 20F): palliative care, hospice election, PPECC attendance.
 * Errors are thrown as ApiError (apiClient); show them with describeApiError.
 */
import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type { PatientPrograms, ProgramName, ProgramRecord } from '../../dtos/patientmr/programs.dto';

const base = (patientId: string) => `/patientmr/${encodeURIComponent(patientId)}/programs`;

function unwrap<T>(response: ApiResponse<T>, fallback: string): T {
  if (response.success && response.data) return response.data;
  throw new Error(response.error?.message || fallback);
}

/** Today's date (local) as YYYY-MM-DD */
const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export class ProgramsService {
  static async get(patientId: string): Promise<PatientPrograms> {
    const response: ApiResponse<PatientPrograms> = await apiClient.get(base(patientId));
    return unwrap(response, 'Failed to load the patient programmes');
  }

  /** Save one programme; pass the version you read so a concurrent edit is refused (409) */
  static async save<T>(patientId: string, program: ProgramName, data: T, expectedVersion?: number | null): Promise<ProgramRecord<T>> {
    const body = { ...data, ...(expectedVersion ? { expectedVersion } : {}) };
    const response: ApiResponse<ProgramRecord<T>> = await apiClient.put(`${base(patientId)}/${program}`, body);
    return unwrap(response, 'Failed to save the programme');
  }

  /** The palliative programme is in effect on a date */
  static palliativeActive(programs: PatientPrograms | null, date = todayIso()): boolean {
    const p = programs?.palliative?.data;
    return Boolean(p?.startDate && p.startDate <= date && (!p.endDate || p.endDate >= date));
  }

  /** The hospice election is in effect on a date */
  static hospiceActive(programs: PatientPrograms | null, date = todayIso()): boolean {
    const h = programs?.hospice?.data;
    return Boolean(h?.electionDate && h.electionDate <= date && (!h.revocationDate || h.revocationDate >= date));
  }
}
